import { useEffect, useMemo, useState } from "react";
import { Table, Input, Select, Button, Popconfirm, Tooltip, Upload, Modal, message, Tag, Space, Alert } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  FileTextOutlined,
  PlusOutlined,
  InboxOutlined,
  CheckOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { TbExternalLink } from "react-icons/tb";
import type { UploadFile } from "antd/es/upload/interface";
import { MiscFile, MiscFileType } from "dtos/misc.file.dto";
import { ApiService } from "api/api.service";

const typeLabels: Record<MiscFileType, string> = {
  [MiscFileType.SETTLEMENT_AGREEMENT]: "Settlement Agreement",
  [MiscFileType.UCC_LIEN]: "UCC Lien",
  [MiscFileType.JUDGMENT]: "Judgment",
  [MiscFileType.EMAIL_CHAIN]: "Email Chain",
  [MiscFileType.PAYMENT_HISTORY]: "Payment History",
  [MiscFileType.ATTORNEY_LETTER]: "Attorney Letter",
  [MiscFileType.SCREENSHOTS]: "Screenshot",
  [MiscFileType.REDEMPTION_LETTER]: "Redemption Letter",
  [MiscFileType.FSP_LOAN_DOC]: "FSP Loan Doc",
  [MiscFileType.OTHERS]: "Other",
};

const typeColors: Record<MiscFileType, string> = {
  [MiscFileType.SETTLEMENT_AGREEMENT]: "green",
  [MiscFileType.UCC_LIEN]: "volcano",
  [MiscFileType.JUDGMENT]: "red",
  [MiscFileType.EMAIL_CHAIN]: "blue",
  [MiscFileType.PAYMENT_HISTORY]: "gold",
  [MiscFileType.ATTORNEY_LETTER]: "purple",
  [MiscFileType.SCREENSHOTS]: "cyan",
  [MiscFileType.REDEMPTION_LETTER]: "magenta",
  [MiscFileType.FSP_LOAN_DOC]: "geekblue",
  [MiscFileType.OTHERS]: "default",
};

const sortFieldOptions: { value: string; label: string }[] = [{ value: "createdAt", label: "Date uploaded" }];

type PendingUpload = {
  file: UploadFile;
  type: MiscFileType | undefined;
  loan_id?: string;
  creditor_id?: string;
};

type LoanOption = { name?: string; funded_amount?: number; _id: string; creditor_id?: string };

const useDebouncedValue = <T,>(value: T, delayMs: number) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
};

export const MiscFiles = ({
  referenceId,
  mode = "application",
  loans,
}: {
  referenceId: string;
  mode: string;
  loans: LoanOption[];
}) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput, 400);
  const [typeFilter, setTypeFilter] = useState<MiscFileType | undefined>();
  const [loanFilter, setLoanFilter] = useState<string | undefined>();
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [editingAliasId, setEditingAliasId] = useState<string | null>(null);
  const [aliasDraft, setAliasDraft] = useState("");

  const [notesModalId, setNotesModalId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState("");

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);

  const loanOptions = useMemo(
    () =>
      loans.map((l) => ({
        value: l._id,
        label: l.funded_amount
          ? `${l.name || `Loan ${l._id.slice(-6)}`} ($${l.funded_amount.toLocaleString()})`
          : l.name || `Loan ${l._id.slice(-6)}`,
      })),
    [loans],
  );
  const loanMap = useMemo(() => Object.fromEntries(loans.map((l) => [l._id, l])), [loans]);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, loanFilter, sortBy, sortOrder]);

  const { data, isLoading } = ApiService.miscFiles.useGetMiscFiles({
    page,
    limit,
    search,
    type: typeFilter,
    sortBy,
    sortOrder,
    referenceId,
    mode,
    loanId: loanFilter,
  });

  const editMiscFile = ApiService.miscFiles.useEditMiscFile();
  const deleteMiscFile = ApiService.miscFiles.useDeleteMiscFile();
  const addMiscFiles = ApiService.miscFiles.useAddMiscFiles();
  const getMiscFileUrl = ApiService.miscFiles.useGetMiscFileUrl();

  const files = data?.result.data ?? [];
  const pagination = data?.result;

  const saveAlias = (id: string) => {
    if (!aliasDraft.trim()) {
      setEditingAliasId(null);
      return;
    }
    editMiscFile.mutate(
      { id, update: { alias: aliasDraft } },
      {
        onSuccess: () => setEditingAliasId(null),
        onError: () => message.error("Could not update alias."),
      },
    );
  };

  const saveType = (id: string, type: MiscFileType) => {
    editMiscFile.mutate({ id, update: { type } }, { onError: () => message.error("Could not update type.") });
  };

  // Tagging a loan on an existing row also auto-fills the matching creditor,
  // since each loan belongs to exactly one creditor.
  const saveLoanTag = (id: string, loan_id: string | undefined) => {
    const creditor_id = loan_id ? loanMap[loan_id]?.creditor_id : undefined;
    editMiscFile.mutate(
      { id, update: { loan_id: loan_id ?? "", creditor_id: creditor_id ?? "" } },
      { onError: () => message.error("Could not tag creditor.") },
    );
  };

  const saveNotes = () => {
    if (!notesModalId) return;
    editMiscFile.mutate(
      { id: notesModalId, update: { notes: notesDraft } },
      {
        onSuccess: () => {
          message.success("Remark saved.");
          setNotesModalId(null);
        },
        onError: () => message.error("Could not update remark."),
      },
    );
  };

  const handleDelete = (id: string) => {
    deleteMiscFile.mutate(
      { id },
      {
        onSuccess: () => message.success("File deleted."),
        onError: () => message.error("Could not delete file."),
      },
    );
  };

  const handleViewFile = (id: string) => {
    getMiscFileUrl.mutate(
      { id },
      {
        onSuccess: (res) => {
          window.open(res.result.url, "_blank", "noopener,noreferrer");
        },
        onError: () => message.error("Could not fetch file url."),
      },
    );
  };

  const missingTypeCount = pendingUploads.filter((p) => !p.type).length;
  const canUpload = pendingUploads.length > 0 && missingTypeCount === 0;

  const handleUploadSubmit = () => {
    if (!canUpload) return;

    const formData = new FormData();
    formData.append("reference_id", referenceId);
    formData.append("mode", mode);

    const tags = pendingUploads.map((p) => ({
      loan_id: p.loan_id || undefined,
      creditor_id: p.loan_id ? loanMap[p.loan_id]?.creditor_id : undefined,
    }));
    formData.append("tags", JSON.stringify(tags));

    pendingUploads.forEach((p) => {
      formData.append("files", p.file.originFileObj as File);
      formData.append("types", p.type as string);
    });

    addMiscFiles.mutate(
      { formData },
      {
        onSuccess: () => {
          message.success("Files uploaded.");
          setUploadModalOpen(false);
          setPendingUploads([]);
        },
        onError: () => message.error("Upload failed."),
      },
    );
  };

  const columns = [
    {
      title: "File",
      dataIndex: "alias",
      key: "file_name",
      render: (_: unknown, record: MiscFile) => {
        const isEditing = editingAliasId === record._id;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            {isEditing ? (
              <Space size={6}>
                <Input
                  size="small"
                  autoFocus
                  value={aliasDraft}
                  disabled={editMiscFile.isPending}
                  onChange={(e) => setAliasDraft(e.target.value)}
                  onPressEnter={() => saveAlias(record._id)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.stopPropagation();
                      setEditingAliasId(null);
                    }
                  }}
                  style={{ width: 180 }}
                />
                <Tooltip title="Save">
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={editMiscFile.isPending}
                    onClick={() => saveAlias(record._id)}
                  />
                </Tooltip>
              </Space>
            ) : (
              <>
                <Tooltip title={record.file_name}>
                  <span
                    style={{
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        maxWidth: 150,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => {
                        setEditingAliasId(record._id);
                        setAliasDraft(record.alias || record.file_name);
                      }}
                    >
                      {record.alias || record.file_name}
                    </span>
                    <EditOutlined
                      style={{ color: "#999", fontSize: 12, flexShrink: 0 }}
                      onClick={() => {
                        setEditingAliasId(record._id);
                        setAliasDraft(record.alias || record.file_name);
                      }}
                    />
                  </span>
                </Tooltip>
                {getMiscFileUrl.isPending && getMiscFileUrl.variables?.id === record._id ? (
                  <LoadingOutlined style={{ fontSize: 15, color: "#1677ff", flexShrink: 0 }} />
                ) : (
                  <TbExternalLink
                    size={15}
                    style={{ cursor: "pointer", color: "#1677ff", flexShrink: 0 }}
                    onClick={() => handleViewFile(record._id)}
                  />
                )}
              </>
            )}
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      // width: 190,
      render: (type: MiscFileType, record: MiscFile) => (
        <Select
          size="small"
          variant="borderless"
          value={type}
          loading={editMiscFile.isPending && editMiscFile.variables?.id === record._id}
          disabled={editMiscFile.isPending && editMiscFile.variables?.id === record._id}
          style={{ width: 170 }}
          onChange={(val) => saveType(record._id, val)}
          popupMatchSelectWidth={false}
          labelRender={() => (
            <Tooltip title={typeLabels[type]}>
              <Tag
                color={typeColors[type]}
                style={{
                  marginRight: 0,
                  maxWidth: 130,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  verticalAlign: "middle",
                }}
              >
                {typeLabels[type]}
              </Tag>
            </Tooltip>
          )}
          options={Object.values(MiscFileType).map((t) => ({
            value: t,
            label: (
              <Tag color={typeColors[t]} style={{ marginRight: 0 }}>
                {typeLabels[t]}
              </Tag>
            ),
          }))}
        />
      ),
    },
    {
      title: "Creditor",
      dataIndex: "loan_id",
      key: "loan_id",
      // width: 170,
      render: (loan_id: string | undefined, record: MiscFile) => (
        <Select
          size="small"
          allowClear
          variant="borderless"
          placeholder="Tag a creditor"
          value={loan_id}
          loading={editMiscFile.isPending && editMiscFile.variables?.id === record._id}
          disabled={editMiscFile.isPending && editMiscFile.variables?.id === record._id}
          style={{ width: 150 }}
          onChange={(val) => saveLoanTag(record._id, val)}
          popupMatchSelectWidth={false}
          options={loanOptions}
          optionRender={(option) => (
            <Tooltip title={option.label} placement="right">
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{option.label}</div>
            </Tooltip>
          )}
        />
      ),
    },
    {
      title: "Uploaded",
      dataIndex: "createdAt",
      key: "createdAt",
      // width: 170,
      render: (createdAt: string) => (
        <span style={{ fontSize: 13, color: "#555" }}>
          {new Date(createdAt).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          })}
        </span>
      ),
    },
    {
      title: "Remark",
      dataIndex: "notes",
      key: "notes",
      // width: 220,
      render: (_: unknown, record: MiscFile) => (
        <Space size={6}>
          {record.notes ? (
            <Tooltip title={record.notes}>
              <span
                style={{
                  color: "#555",
                  maxWidth: 140,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              >
                {record.notes}
              </span>
            </Tooltip>
          ) : (
            <span style={{ color: "#bbb" }}>No remarks</span>
          )}
          <Button
            size="small"
            type="text"
            icon={record.notes ? <FileTextOutlined /> : <PlusOutlined />}
            disabled={editMiscFile.isPending && editMiscFile.variables?.id === record._id}
            onClick={() => {
              setNotesModalId(record._id);
              setNotesDraft(record.notes || "");
            }}
          />
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: unknown, record: MiscFile) => (
        <Space size={6}>
          <Tooltip title="Open file">
            <Button
              size="small"
              icon={<TbExternalLink size={14} style={{ color: "#1677ff" }} />}
              loading={getMiscFileUrl.isPending && getMiscFileUrl.variables?.id === record._id}
              onClick={() => handleViewFile(record._id)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this file?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record._id)}
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deleteMiscFile.isPending && deleteMiscFile.variables?.id === record._id}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }} wrap>
        <Space wrap>
          <Input
            placeholder="Search by file name"
            allowClear
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ width: 240 }}
          />
          <Select
            allowClear
            placeholder="Filter by type"
            style={{ width: 190 }}
            value={typeFilter}
            onChange={(val) => setTypeFilter(val)}
            options={Object.values(MiscFileType).map((t) => ({ value: t, label: typeLabels[t] }))}
          />
          <Select
            allowClear
            placeholder="Filter by creditor"
            style={{ width: 190 }}
            value={loanFilter}
            onChange={(val) => setLoanFilter(val)}
            options={loanOptions}
            optionRender={(option) => (
              <Tooltip title={option.label} placement="right">
                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{option.label}</div>
              </Tooltip>
            )}
          />

          <Space.Compact>
            <Select
              value={sortBy}
              style={{ width: 150 }}
              onChange={(val) => setSortBy(val)}
              options={sortFieldOptions}
            />
            <Tooltip title={sortOrder === "asc" ? "Ascending" : "Descending"}>
              <Button
                icon={sortOrder === "asc" ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
                onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
              />
            </Tooltip>
          </Space.Compact>
        </Space>
        <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadModalOpen(true)}>
          Upload Files
        </Button>
      </Space>

      <Table
        rowKey="_id"
        loading={isLoading}
        columns={columns}
        dataSource={files}
        scroll={{ x: "max-content" }}
        pagination={{
          current: pagination?.page ?? 1,
          pageSize: pagination?.limit ?? limit,
          total: pagination?.totalObjects ?? 0,
          showSizeChanger: true,
          onChange: (p, l) => {
            setPage(p);
            setLimit(l);
          },
        }}
      />

      <Modal
        title="Remark"
        open={!!notesModalId}
        onCancel={() => setNotesModalId(null)}
        onOk={saveNotes}
        confirmLoading={editMiscFile.isPending}
        okText="Save"
      >
        <Input.TextArea
          rows={5}
          autoFocus
          disabled={editMiscFile.isPending}
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          placeholder="Add a remark for this file..."
        />
      </Modal>

      <Modal
        title="Upload files"
        open={uploadModalOpen}
        onCancel={() => {
          setUploadModalOpen(false);
          setPendingUploads([]);
        }}
        onOk={handleUploadSubmit}
        confirmLoading={addMiscFiles.isPending}
        okText="Upload"
        okButtonProps={{ disabled: !canUpload }}
        width={820}
      >
        <div style={{ display: "flex", gap: 20, height: 420 }}>
          <div style={{ flex: "0 0 260px" }}>
            <Upload.Dragger
              multiple
              fileList={pendingUploads.map((p) => p.file)}
              beforeUpload={(file, fileListBatch) => {
                const remainingSlots = 5 - pendingUploads.length;
                const indexInBatch = fileListBatch.indexOf(file);

                if (indexInBatch >= remainingSlots) {
                  if (indexInBatch === Math.max(remainingSlots, 0)) {
                    message.error("Only 5 files can be uploaded at a time. Extra files were discarded.");
                  }
                  return Upload.LIST_IGNORE;
                }

                return false;
              }}
              showUploadList={false}
              disabled={addMiscFiles.isPending}
              onChange={({ fileList }) => {
                setPendingUploads((prev) =>
                  fileList.map((file) => {
                    const existing = prev.find((p) => p.file.uid === file.uid);
                    return { file, type: existing?.type, loan_id: existing?.loan_id };
                  }),
                );
              }}
              style={{ height: "100%", display: "flex", alignItems: "center" }}
            >
              <div>
                <p className="ant-upload-drag-icon" style={{ margin: 0 }}>
                  <InboxOutlined style={{ fontSize: 28 }} />
                </p>
                <p style={{ margin: "8px 0 0", fontSize: 13 }}>Click or drag files here</p>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: "#999" }}>Up to 5 files</p>
              </div>
            </Upload.Dragger>
          </div>

          <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            {missingTypeCount > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 10 }}
                message={`${missingTypeCount} file${missingTypeCount > 1 ? "s are" : " is"} missing a type. Fill them in below to upload.`}
              />
            )}

            <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
              {pendingUploads.length ? (
                <Space direction="vertical" style={{ width: "100%" }} size={8}>
                  {pendingUploads.map((p) => (
                    <div
                      key={p.file.uid}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 10px",
                        border: !p.type ? "1px solid #ffd591" : "1px solid #f0f0f0",
                        borderRadius: 6,
                        background: !p.type ? "#fffbe6" : "transparent",
                      }}
                    >
                      <Tooltip title={p.file.name}>
                        <span
                          style={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                            minWidth: 0,
                          }}
                        >
                          {p.file.name}
                        </span>
                      </Tooltip>
                      <Select
                        size="small"
                        placeholder="Select type"
                        style={{ width: 180 }}
                        value={p.type}
                        disabled={addMiscFiles.isPending}
                        onChange={(val) =>
                          setPendingUploads((prev) =>
                            prev.map((item) => (item.file.uid === p.file.uid ? { ...item, type: val } : item)),
                          )
                        }
                        options={Object.values(MiscFileType).map((t) => ({ value: t, label: typeLabels[t] }))}
                      />
                      <Select
                        size="small"
                        allowClear
                        placeholder="Tag creditor (optional)"
                        style={{ width: 160 }}
                        value={p.loan_id}
                        disabled={addMiscFiles.isPending}
                        onChange={(val) =>
                          setPendingUploads((prev) =>
                            prev.map((item) => (item.file.uid === p.file.uid ? { ...item, loan_id: val } : item)),
                          )
                        }
                        options={loanOptions}
                        optionRender={(option) => (
                          <Tooltip title={option.label} placement="right">
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {option.label}
                            </div>
                          </Tooltip>
                        )}
                      />
                      <DeleteOutlined
                        style={{ color: "#999", cursor: "pointer" }}
                        onClick={() => setPendingUploads((prev) => prev.filter((item) => item.file.uid !== p.file.uid))}
                      />
                    </div>
                  ))}
                </Space>
              ) : (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#bbb",
                    fontSize: 13,
                  }}
                >
                  No files added yet
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
